import React from 'react';
import { BrowserRouter, Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { Toaster } from '@/components/ui/sonner';
import { AuthProvider, useAuth } from '@/context/AuthContext';
import { LanguageProvider } from '@/context/LanguageContext';
import { AppShell } from '@/components/AppShell';
import Landing from '@/pages/Landing';
import Login from '@/pages/Login';
import Register from '@/pages/Register';
import Studio from '@/pages/Studio';
import Gallery from '@/pages/Gallery';
import Account from '@/pages/Account';
import Admin from '@/pages/Admin';
import AuthCallback from '@/pages/AuthCallback';
import { Loader2 } from 'lucide-react';
import '@/App.css';

const FullLoader = () => (
  <div className="grid min-h-screen place-items-center bg-[hsl(var(--bg))]">
    <Loader2 className="h-7 w-7 animate-spin text-[hsl(var(--brand-red))]" />
  </div>
);

const Protected = ({ children, adminOnly = false }) => {
  const { user, loading } = useAuth();
  if (loading) return <FullLoader />;
  if (!user) return <Navigate to="/giris" replace />;
  if (adminOnly && user.role !== 'admin') return <Navigate to="/studyo" replace />;
  return <AppShell>{children}</AppShell>;
};

const GuestOnly = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) return <FullLoader />;
  if (user) return <Navigate to="/studyo" replace />;
  return children;
};

function AppRouter() {
  // Read the fragment from useLocation() (reactive) - never window.location.hash here.
  const location = useLocation();
  if (location.hash?.includes('session_id=')) return <AuthCallback />;

  return (
    <Routes>
      <Route path="/" element={<Landing />} />
      <Route path="/giris" element={<GuestOnly><Login /></GuestOnly>} />
      <Route path="/kayit" element={<GuestOnly><Register /></GuestOnly>} />
      <Route path="/studyo" element={<Protected><Studio /></Protected>} />
      <Route path="/galeri" element={<Protected><Gallery /></Protected>} />
      <Route path="/hesabim" element={<Protected><Account /></Protected>} />
      <Route path="/admin" element={<Protected adminOnly><Admin /></Protected>} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <div className="App">
      <LanguageProvider>
        <AuthProvider>
          <BrowserRouter>
            <AppRouter />
          </BrowserRouter>
          <Toaster position="top-right" richColors theme="dark" />
        </AuthProvider>
      </LanguageProvider>
    </div>
  );
}

export default App;
